import React, { useMemo, useState } from 'react';
import { CalendarDaysIcon, InboxIcon, FileTextIcon, BuildingIcon, UserIcon, ChevronRightIcon, RefreshCwIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ResourceCalendar } from './ResourceCalendar';
import { BookingDetail } from './BookingDetail';
import { BookingForm } from './BookingForm';
import { ContractsQueue } from './ContractsQueue';
import { BOOKING_FILES, CONTRACTS } from '../../data/windsor';
import { bookingStatusMeta, findUsageType, paymentMeta, paymentMethodLabel, resourcePath, usageTint } from './bookingHelpers';
import type { BookingFile } from '../../types/loisirs';
interface Props {
  query: string;
  /** True while the "New booking" flow is open. */
  creating: boolean;
  onCloseCreate: () => void;
}
type View = 'calendrier' | 'demandes' | 'contrats';
type RequesterFilter = 'tous' | 'organisation' | 'citoyen' | 'interac';
export function BookingsSection({
  query,
  creating,
  onCloseCreate
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [view, setView] = useState<View>('calendrier');
  const [open, setOpen] = useState<BookingFile | null>(null);
  const [filter, setFilter] = useState<RequesterFilter>('tous');
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  const shortDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  const interacCount = BOOKING_FILES.filter(b => b.payment.status === 'virement_attendu').length;
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return BOOKING_FILES.filter(b => {
      if (filter === 'interac') return b.payment.status === 'virement_attendu';
      if (filter === 'tous') return true;
      return b.client.kind === filter;
    }).filter(b => q ? b.client.name.toLowerCase().includes(q) || String(b.number).includes(q) || resourcePath(b.resourceId, lang).toLowerCase().includes(q) : true);
  }, [filter, query, lang]);
  if (creating) {
    return <BookingForm onCancel={onCloseCreate} onSaved={() => {
      onCloseCreate();
      setView('demandes');
    }} data-id="element-2915" data-mp="w9hmmnnxnqx5" />;
  }
  if (open) {
    return <BookingDetail booking={open} onBack={() => setOpen(null)} data-id="element-2916" data-mp="116rnczgrvp0a" />;
  }
  const views: {
    id: View;
    label: string;
    icon: typeof CalendarDaysIcon;
    count?: number;
  }[] = [{
    id: 'calendrier',
    label: t('Calendrier', 'Calendar'),
    icon: CalendarDaysIcon
  }, {
    id: 'demandes',
    label: t('Demandes', 'Requests'),
    icon: InboxIcon,
    count: BOOKING_FILES.filter(b => b.status === 'brouillon').length
  }, {
    id: 'contrats',
    label: t('Contrats', 'Contracts'),
    icon: FileTextIcon,
    count: CONTRACTS.filter(c => c.status === 'a_generer' || c.status === 'en_retard').length
  }];
  return <div className="space-y-4" data-id="element-2917" data-mp="2zhrw16h0923u">
      {/* Sub-navigation */}
      <div className="border-b border-slate-200" data-id="element-2918" data-mp="2zk47r1w66g8g">
        <nav aria-label={t('Vues des réservations', 'Booking views')} className="flex items-center gap-1" data-id="element-2919" data-mp="2ujqb0g00qspq">
          {views.map(item => {
          const Icon = item.icon;
          const active = view === item.id;
          return <button key={item.id} onClick={() => setView(item.id)} className={`inline-flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${active ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-id="element-2920" data-mp="3igvwvrye7pkf">
                <Icon className="w-4 h-4" data-id="element-2921" data-mp="3qo739jxt761e" />
                {item.label}
                {item.count !== undefined && item.count > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${active ? 'bg-indigo-50 text-[#4F46E5]' : 'bg-slate-100 text-slate-500'}`} data-id="element-2922" data-mp="3vlgsayhadfop">
                    {item.count}
                  </span>}
              </button>;
        })}
        </nav>
      </div>

      {view === 'calendrier' && <ResourceCalendar onOpenBooking={setOpen} data-id="element-2923" data-mp="3izgw6z41v9p" />}

      {view === 'contrats' && <ContractsQueue query={query} onOpenBooking={setOpen} data-id="element-2924" data-mp="8hshiytatrsg" />}

      {view === 'demandes' && <div className="space-y-4" data-id="element-2925" data-mp="w7dbecqzq9gi">
          <div className="flex flex-wrap items-center gap-2" data-id="element-2926" data-mp="w6bqzho34am4">
            {[{
          id: 'tous' as const,
          label: t('Tous', 'All')
        }, {
          id: 'organisation' as const,
          label: t('Organismes', 'Organizations')
        }, {
          id: 'citoyen' as const,
          label: t('Citoyens', 'Citizens')
        }].map(item => <button key={item.id} onClick={() => setFilter(item.id)} className={`px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${filter === item.id ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-2927" data-mp="2qgim9p8i3rb1">
                {item.label}
              </button>)}
            <button onClick={() => setFilter('interac')} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${filter === 'interac' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50'}`} data-id="element-2928" data-mp="3n6j4f5ed79jd">
              <RefreshCwIcon className="w-4 h-4" data-id="element-2929" data-mp="2prog2p8hcr2g" />
              {t('Virements Interac à réconcilier', 'Interac transfers to reconcile')}
              <span className="text-xs font-bold tabular-nums" data-id="element-2930" data-mp="2tsi19aujfrrn">
                {interacCount}
              </span>
            </button>
          </div>

          {visible.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center" data-id="element-2931" data-mp="223ezc1q4iti6">
              <div className="mx-auto w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3" data-id="element-2932" data-mp="2635vym2c03um">
                <InboxIcon className="w-6 h-6 text-slate-400" data-id="element-2933" data-mp="2avqlhbe7fusc" />
              </div>
              <p className="text-sm font-semibold text-slate-700" data-id="element-2934" data-mp="1ebzthyz51u26">
                {t('Aucune demande à afficher', 'No request to display')}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-2935" data-mp="1av510kaqql1r">
                {t('Ajustez les filtres ou la recherche.', 'Adjust the filters or the search.')}
              </p>
            </div> : <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-2936" data-mp="11svtm3arr7sl">
              <div className="overflow-x-auto" data-id="element-2937" data-mp="wukizqcngt9b">
                <table className="w-full text-sm text-left" data-id="element-2938" data-mp="rw995fmcuir3">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-2939" data-mp="rxu61bw56ds4">
                    <tr data-id="element-2940" data-mp="n3g3mpj2b6dy">
                      <th className="px-5 py-3 font-medium" data-id="element-2941" data-mp="i6fz4j1v2boy">
                        {t('Client', 'Client')}
                      </th>
                      <th className="px-5 py-3 font-medium" data-id="element-2942" data-mp="e5vz97pwktz4">
                        {t('Ressource', 'Resource')}
                      </th>
                      <th className="px-5 py-3 font-medium" data-id="element-2943" data-mp="97ug6msh8unn">
                        {t('Utilisation', 'Usage')}
                      </th>
                      <th className="px-5 py-3 font-medium" data-id="element-2944" data-mp="1s2uyjs9syau5">
                        {t('Période', 'Period')}
                      </th>
                      <th className="px-5 py-3 font-medium" data-id="element-2945" data-mp="1x1xr4d8liol8">
                        {t('Solde', 'Balance')}
                      </th>
                      <th className="px-5 py-3 font-medium" data-id="element-2946" data-mp="1o1rh51c3w6qt">
                        {t('Paiement', 'Payment')}
                      </th>
                      <th className="px-5 py-3" data-id="element-2947" data-mp="19btyf0wy4990" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-2948" data-mp="1e4n35osezhl3">
                    {visible.map(booking => {
                const usage = findUsageType(booking.usageTypeId);
                const status = bookingStatusMeta(booking.status);
                const pay = paymentMeta(booking.payment.status);
                return <tr key={booking.id} onClick={() => setOpen(booking)} tabIndex={0} role="button" onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpen(booking);
                  }
                }} className="group cursor-pointer hover:bg-slate-50/80 focus:outline-none focus:bg-indigo-50/50 transition-colors" data-id="element-2949" data-mp="vd40xk67s7k4">
                          <td className="px-5 py-4" data-id="element-2950" data-mp="pqbml9z58b41">
                            <div className="flex items-center gap-2.5" data-id="element-2951" data-mp="14fzmq7vl93q6">
                              <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${booking.client.kind === 'organisation' ? 'bg-indigo-50 text-[#4F46E5]' : 'bg-slate-100 text-slate-500'}`} data-id="element-2952" data-mp="14f7vf990xs5p">
                                {booking.client.kind === 'organisation' ? <BuildingIcon className="w-3.5 h-3.5" data-id="element-2953" data-mp="1pyq1a1uz6yme" /> : <UserIcon className="w-3.5 h-3.5" data-id="element-2954" data-mp="1kzdf7jbxhvkv" />}
                              </span>
                              <div className="min-w-0" data-id="element-2955" data-mp="2dqncpruijomz">
                                <p className="font-medium text-slate-900 truncate max-w-[220px]" data-id="element-2956" data-mp="2do16pwn9th6v">
                                  {booking.client.name}
                                </p>
                                <p className="text-xs text-slate-400 tabular-nums" data-id="element-2957" data-mp="23ualr1qcrovx">
                                  #{booking.number}
                                  {' · '}
                                  <span className={`inline-flex px-1.5 rounded ${status.className}`} data-id="element-2958" data-mp="selye996snul">
                                    {status[lang]}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-4 text-slate-600 max-w-[200px]" data-id="element-2959" data-mp="38xxqqix2ujm6">
                            <span className="block truncate" data-id="element-2960" data-mp="3dr8xkk1w30e3">
                              {resourcePath(booking.resourceId, lang)}
                            </span>
                          </td>

                          <td className="px-5 py-4" data-id="element-2961" data-mp="3nnc2tys1ff59">
                            {usage && <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border" style={usageTint(usage.color)} data-id="element-2962" data-mp="3rr1mc9tuv6jf">
                                <span className="w-1.5 h-1.5 rounded-full" style={{
                        backgroundColor: usage.color
                      }} data-id="element-2963" data-mp="22b29ahn48wyi" />
                                {usage.label[lang]}
                              </span>}
                          </td>

                          <td className="px-5 py-4 text-slate-600 whitespace-nowrap" data-id="element-2964" data-mp="3enxpngqrd74a">
                            <p data-id="element-2965" data-mp="39lzxty4al3zq">{shortDate(booking.startDate)}</p>
                            <p className="text-xs text-slate-400" data-id="element-2966" data-mp="3okajcemazkz1">
                              {booking.slots.length}{' '}
                              {t('plages', 'slots')}
                            </p>
                          </td>

                          <td className="px-5 py-4 tabular-nums whitespace-nowrap" data-id="element-2967" data-mp="16ycwu3k5q8y">
                            <p className={`font-medium ${booking.billing.balance > 0 ? 'text-amber-700' : 'text-slate-500'}`} data-id="element-2968" data-mp="3tctsmzdyjty1">
                              {money(booking.billing.balance)}
                            </p>
                            <p className="text-xs text-slate-400" data-id="element-2969" data-mp="uqxvorgp2dlm">
                              {t('sur ', 'of ')}
                              {money(booking.billing.total)}
                            </p>
                          </td>

                          <td className="px-5 py-4" data-id="element-2970" data-mp="72en0atqfyt0">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${pay.className}`} data-id="element-2971" data-mp="70k38zu76cqi">
                              {pay[lang]}
                            </span>
                            <p className="text-xs text-slate-400 mt-0.5" data-id="element-2972" data-mp="23tmeaakjd41">
                              {paymentMethodLabel(booking.payment.method)[lang]}
                            </p>
                          </td>

                          <td className="px-5 py-4" data-id="element-2973" data-mp="3o9grd6cht6ua">
                            <ChevronRightIcon className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2974" data-mp="3h7la09ne9k9c" />
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>
            </div>}

          {filter === 'interac' && interacCount > 0 && <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-5 py-4" data-id="element-2975" data-mp="2ygal1y18bhk3">
              <p className="text-sm text-amber-800" data-id="element-2976" data-mp="2thqzvzugm9ss">
                {t('Les virements Interac sont réconciliés automatiquement lorsque le dépôt est confirmé par l’institution financière. Une relance est envoyée après 48 heures sans réception.', 'Interac transfers are reconciled automatically once the deposit is confirmed by the financial institution. A reminder is sent after 48 hours without receipt.')}
              </p>
            </div>}
        </div>}
    </div>;
}